// "VIEWPOINTS TO ISSUES" (owner 2026-10-23): a stored Navisworks file's saved viewpoints,
// grouped by folder, searchable, select-all per folder; a target project (this one by
// default); Submit queues one heavy-worker run that raises one issue per viewpoint -
// picture, camera, visible elements - waiting in the target's Review Center triage tab
// (back/routes/projectModels.js, worker/processors/viewpointIssueProcessor.js).
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Input, Checkbox, Alert, Spin, Tag, Tooltip } from "antd";
import addNotification from "helpers/notify";
import { matchOption } from "components/SearchSelect/searchSelect";
import { listViewpoints, createViewpointIssues } from "api/projectModels";

const NO_FOLDER = "(no folder)";

export default function ViewpointIssuesModal({ open, projectId, file, projects, onClose, onQueued }) {
  const [target, setTarget] = useState(projectId);
  const [viewpoints, setViewpoints] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTarget(projectId);
      setPicked(new Set());
      setSearch("");
    }
  }, [open, projectId, file]);

  useEffect(() => {
    if (!open || !file || !target) return undefined;
    let alive = true;
    setViewpoints(null);
    setError("");
    listViewpoints(projectId, file._id, target)
      .then((out) => alive && setViewpoints(out.viewpoints || []))
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        setViewpoints([]);
      });
    return () => {
      alive = false;
    };
  }, [open, file, projectId, target]);

  // the ones already issues on the target cannot be picked again
  useEffect(() => {
    if (!viewpoints) return;
    setPicked((p) => new Set([...p].filter((k) => viewpoints.some((v) => v.key === k && !v.issue))));
  }, [viewpoints]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map();
    (viewpoints || [])
      .filter((v) => !q || `${v.folder} ${v.name}`.toLowerCase().includes(q))
      .forEach((v) => {
        const f = v.folder || NO_FOLDER;
        if (!map.has(f)) map.set(f, []);
        map.get(f).push(v);
      });
    return [...map.entries()];
  }, [viewpoints, search]);

  const toggle = (keys, on) =>
    setPicked((p) => {
      const n = new Set(p);
      keys.forEach((k) => (on ? n.add(k) : n.delete(k)));
      return n;
    });

  const targetName = (projects.find((p) => p._id === target) || {}).title || "the target project";
  const shown = groups.flatMap(([, vs]) => vs.filter((v) => !v.issue).map((v) => v.key));
  const already = (viewpoints || []).filter((v) => v.issue).length;

  const submit = async () => {
    const chosen = (viewpoints || []).filter((v) => picked.has(v.key));
    setBusy(true);
    try {
      const out = await createViewpointIssues(projectId, file._id, {
        targetProjectId: target,
        keys: chosen.map((v) => v.key),
        names: chosen.map((v) => v.name),
      });
      if (out.queued) {
        addNotification(`${out.keys} viewpoint${out.keys === 1 ? "" : "s"} on the way to ${targetName} as issues - they wait in its Review Center triage tab`, "success");
      } else {
        addNotification(out.skipped === "already created" ? "Those viewpoints are already issues on that project" : `Not queued: ${out.skipped}`, "warning");
      }
      if (out.already && out.already.length && out.queued) addNotification(`${out.already.length} already created - not raised again`, "info");
      onQueued && onQueued(out);
      onClose();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Viewpoints to issues${file ? ` - ${file.name}` : ""}`}
      onCancel={onClose}
      onOk={submit}
      okText={picked.size ? `Create ${picked.size} issue${picked.size === 1 ? "" : "s"}` : "Create issues"}
      okButtonProps={{ disabled: !picked.size || busy || !target, loading: busy }}
      width={680}
      destroyOnClose
      wrapClassName="vp-issues-modal"
    >
      <div className="vp-issues-target">
        <span>Raise them on</span>
        <Select
          showSearch
          filterOption={matchOption}
          optionFilterProp="label"
          style={{ minWidth: 280 }}
          value={target || undefined}
          onChange={setTarget}
          aria-label="Target project"
          options={projects.map((p) => ({ value: p._id, label: p.title }))}
        />
      </div>
      <Alert
        type="info"
        showIcon
        className="mb-2"
        message={`Each viewpoint becomes one issue on ${targetName} - its picture, camera and the elements visible in it - created by you, assigned to no one. They wait in the project's Review Center triage tab until its owner confirms them.`}
      />
      <div className="vp-issues-tools">
        <Input.Search allowClear placeholder="Search viewpoints" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
        <span className="vp-issues-spacer" />
        <Tooltip title="Tick every viewpoint the search is showing">
          <button type="button" className="integration-link-btn" disabled={!shown.length} onClick={() => toggle(shown, true)}>
            Select all shown
          </button>
        </Tooltip>
        <Tooltip title="Untick every viewpoint">
          <button type="button" className="integration-link-btn" disabled={!picked.size} onClick={() => setPicked(new Set())}>
            Clear
          </button>
        </Tooltip>
      </div>
      {error ? <Alert type="error" showIcon className="mb-2" message={error} /> : null}
      {viewpoints === null ? (
        <div className="text-center py-4">
          <Spin tip="Reading the saved viewpoints..." />
        </div>
      ) : !viewpoints.length && !error ? (
        <p className="text-muted py-3">This file has no saved viewpoints.</p>
      ) : (
        <div className="vp-issues-list">
          {groups.map(([folder, vs]) => {
            const free = vs.filter((v) => !v.issue).map((v) => v.key);
            const on = free.filter((k) => picked.has(k)).length;
            return (
              <div key={folder} className="vp-issues-folder">
                <Checkbox
                  checked={!!free.length && on === free.length}
                  indeterminate={on > 0 && on < free.length}
                  disabled={!free.length}
                  onChange={(e) => toggle(free, e.target.checked)}
                  aria-label={`All in ${folder}`}
                >
                  <b>{folder}</b> <small className="text-muted">({vs.length})</small>
                </Checkbox>
                {vs.map((v) => (
                  <div key={v.key} className="vp-issues-item">
                    <Checkbox checked={picked.has(v.key)} disabled={!!v.issue} onChange={(e) => toggle([v.key], e.target.checked)}>
                      {v.name}
                    </Checkbox>
                    {v.orthographic ? <Tag>ortho</Tag> : null}
                    {!v.hasCamera ? <Tag color="gold">no camera</Tag> : null}
                    {v.issue ? (
                      <Tooltip title={v.issue.title}>
                        <Tag color="green">Already issue {v.issue.displayId || ""}</Tag>
                      </Tooltip>
                    ) : null}
                  </div>
                ))}
              </div>
            );
          })}
          {!groups.length ? <p className="text-muted py-2">No viewpoint matches the search.</p> : null}
        </div>
      )}
      {already ? <small className="text-muted d-block mt-2">{already} already an issue on this project - not raised again.</small> : null}
    </Modal>
  );
}
